/**
 * useAuth Hook
 *
 * Provides a convenient interface for auth state and actions.
 * Uses the Zustand auth store for state management.
 *
 * This hook is the primary interface for components to interact
 * with authentication. It handles:
 * - Current user and authentication status
 * - Login/logout actions
 * - Token refresh
 * - JWT payload extraction
 *
 * @module hooks/useAuth
 */

import { useCallback, useMemo } from 'react';
import {
  useAuthStore,
  selectIsAuthenticated,
  selectHasBusiness,
  selectIsLoading,
} from '@/stores/authStore';
import type { AuthTokens, AuthUser, UserRole } from '@/stores/authStore';

/**
 * Auth state and actions returned by useAuth hook
 */
interface UseAuthReturn {
  /** Current user info (null if not authenticated) */
  user: AuthUser | null;
  /** Whether the user is authenticated */
  isAuthenticated: boolean;
  /** Whether auth state is loading (validating or refreshing) */
  isLoading: boolean;
  /** Whether user has completed business setup */
  hasBusiness: boolean;
  /** Current business ID (null if no business) */
  businessId: string | null;
  /** Current user ID (null if not authenticated) */
  userId: string | null;
  /** Current access token (null if not authenticated) */
  accessToken: string | null;
  /** Login action - sets tokens and user */
  login: (tokens: AuthTokens, user: AuthUser) => void;
  /** Logout action - clears all auth state */
  logout: () => void;
  /** Check if user has a specific role */
  hasRole: (role: UserRole) => boolean;
}

/**
 * Decode JWT payload without verification (client-side only)
 * For actual verification, the server validates the token
 *
 * @param token - JWT token string
 * @returns Decoded payload or null if invalid
 */
export function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;

    const base64Url = parts[1];
    if (!base64Url) return null;

    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/**
 * Extract user info from JWT claims
 *
 * @param token - JWT token string
 * @returns User info or null if token is invalid
 */
export function extractUserFromToken(token: string): AuthUser | null {
  const payload = decodeJwtPayload(token);
  if (payload === null) return null;

  // JWT may contain "role" (singular string) or "roles" (array)
  const roles: UserRole[] = Array.isArray(payload.roles)
    ? (payload.roles as UserRole[])
    : typeof payload.role === 'string'
      ? [payload.role as UserRole]
      : [];

  return {
    id: (payload.sub as string) ?? '',
    phone: (payload.phone as string) ?? '',
    email: (payload.email as string) ?? null,
    name: (payload.name as string) ?? null,
    businessId: (payload.business_id as string) ?? null,
    businessName: (payload.business_name as string) ?? null,
    partnerId: (payload.partner_id as string) ?? null,
    roles,
  };
}

/**
 * Calculate token expiration timestamp from expiresIn seconds
 *
 * @param expiresIn - Token lifetime in seconds
 * @returns Expiration timestamp (ms since epoch)
 */
export function calculateExpiresAt(expiresIn: number): number {
  return Date.now() + expiresIn * 1000;
}

/**
 * Hook for authentication state and actions
 *
 * Provides a unified interface for components to access auth state
 * and perform auth actions. Uses the Zustand auth store internally.
 *
 * @example
 * ```tsx
 * function ProfilePage() {
 *   const { user, isAuthenticated, logout, hasRole } = useAuth();
 *
 *   if (!isAuthenticated) {
 *     return <Navigate to="/auth/login" />;
 *   }
 *
 *   return (
 *     <div>
 *       <p>Welcome, {user?.name ?? user?.phone}</p>
 *       {hasRole('OWNER') && <AdminPanel />}
 *       <button onClick={logout}>Logout</button>
 *     </div>
 *   );
 * }
 * ```
 */
export function useAuth(): UseAuthReturn {
  // Get state from store using selectors for optimal re-renders
  const user = useAuthStore((state) => state.user);
  const tokens = useAuthStore((state) => state.tokens);
  const isAuthenticated = useAuthStore(selectIsAuthenticated);
  const isLoading = useAuthStore(selectIsLoading);
  const hasBusiness = useAuthStore(selectHasBusiness);

  // Get actions from store
  const setTokens = useAuthStore((state) => state.setTokens);
  const setUser = useAuthStore((state) => state.setUser);
  const storeLogout = useAuthStore((state) => state.logout);

  /**
   * Login action - sets tokens and user in store
   */
  const login = useCallback(
    (authTokens: AuthTokens, authUser: AuthUser) => {
      setTokens(authTokens);
      setUser(authUser);
    },
    [setTokens, setUser]
  );

  /**
   * Logout action - clears all auth state
   */
  const logout = useCallback(() => {
    storeLogout();
  }, [storeLogout]);

  /**
   * Check if user has a specific role
   */
  const hasRole = useCallback(
    (role: UserRole): boolean => {
      return user?.roles.includes(role) ?? false;
    },
    [user?.roles]
  );

  // Memoize derived values
  const businessId = useMemo(() => user?.businessId ?? null, [user?.businessId]);
  const userId = useMemo(() => user?.id ?? null, [user?.id]);
  const accessToken = useMemo(
    () => tokens?.accessToken ?? null,
    [tokens?.accessToken]
  );

  return {
    user,
    isAuthenticated,
    isLoading,
    hasBusiness,
    businessId,
    userId,
    accessToken,
    login,
    logout,
    hasRole,
  };
}
